/**
 * TREN 1.1 (2026-09-11) — sincronía de la comanda SIN pisar capturas.
 *
 * La regresión del smoke ("cocina recibe un envío POR PLATILLO") fue un
 * lost-update: el poll de la comanda (7 s, V131) hacía `setOrder(respuesta)`
 * SIN guardas — una respuesta VIEJA (leída antes de agregar platillos, y en
 * vuelo ~0.5 s por la latencia de staging) aterrizaba DESPUÉS de los agregados
 * y borraba renglones de la vista; el mesero enviaba lo que veía → N envíos.
 *
 * Reglas puras (testeables):
 *  - Un poll iniciado en la mutación #s SOLO se aplica si al llegar seguimos
 *    en #s (ninguna captura local en medio). Si no, se DESCARTA (el próximo
 *    poll trae la verdad).
 *  - "Enviar a cocina" trabaja sobre una lectura FRESCA del servidor: lo que
 *    se imprime == lo que se sella, TODOS los renglones pendientes al momento
 *    del envío (una comanda = un envío).
 */

export interface SyncedItem { id: string; firedAt?: string | null }
export interface SyncedOrder { id: string; items: SyncedItem[] }

/** true = la respuesta del poll sigue siendo válida (cero mutaciones en vuelo). */
export function pollStillValid(seqAtFetchStart: number, seqNow: number): boolean {
  return seqAtFetchStart === seqNow;
}

/** Aplica la respuesta de un poll SOLO si no hubo mutaciones locales en vuelo. */
export function applyPollResponse<T extends SyncedOrder>(
  current: T, incoming: T, seqAtFetchStart: number, seqNow: number,
): T {
  return pollStillValid(seqAtFetchStart, seqNow) ? incoming : current;
}

/** Renglones AÚN no enviados a cocina (la unidad del próximo envío). */
export function pendingForFire<T extends SyncedItem>(items: T[]): T[] {
  return items.filter((it) => !it.firedAt);
}

/**
 * Corrección 11/09 — el BATCH lo decide el SERVIDOR: /fire sella TODOS los
 * pendientes con UN timestamp e ignora cualquier lista del cliente (un estado
 * viejo ya no puede partir la comanda). El ticket se imprime DEL RESPONSE:
 * los renglones recién sellados = los que comparten el fired_at MÁS NUEVO.
 */
export function batchJustFired<T extends SyncedItem>(items: T[]): T[] {
  const fired = items.filter((it) => it.firedAt);
  if (!fired.length) return [];
  const newest = fired.map((it) => String(it.firedAt)).sort().at(-1)!;
  return fired.filter((it) => String(it.firedAt) === newest);
}
